import { useEffect, useState } from "react";
import { api, ApiError, browserTz, type DaySummary } from "../lib/api";
import { fillMissingDays } from "../lib/dates";
import { TodayRings } from "../components/TodayRings";
import { WeeklyChart } from "../components/WeeklyChart";
import { MonthHeatmap } from "../components/MonthHeatmap";
import { StatChips } from "../components/StatChips";
import { TrendChart } from "../components/TrendChart";
import { Spinner } from "../components/Spinner";

export function Home() {
  const [days, setDays] = useState<DaySummary[]>([]);
  const [goal, setGoal] = useState({ calories: 2200, protein_g: 140 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const tz = browserTz();

  useEffect(() => {
    let alive = true;
    api.summary(90, tz).then((r) => {
      if (!alive) return;
      setDays(r.days);
      setGoal(r.goal);
      setLoading(false);
    }).catch((err) => {
      if (!alive) return;
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [tz]);

  if (loading)
    return (
      <div className="loading">
        <Spinner />
      </div>
    );
  if (error) return <div className="error">Failed to load summary — {error}</div>;

  const todayKey = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date());
  const today = days.find((d) => d.day === todayKey) ?? {
    day: todayKey,
    calories: 0,
    protein_g: 0,
    meals: 0,
  };

  const last30 = fillMissingDays(days, 30, tz);

  const minMonth: Date = (() => {
    if (days.length > 0) {
      const parts = days[0]!.day.split("-");
      return new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
    }
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  })();

  const clampedSetMonth = (d: Date) => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    if (d < minMonth || d > currentMonthStart) return;
    setSelectedMonth(d);
  };

  return (
    <div className="home">
      <section className="card">
        <h2>Today</h2>
        <TodayRings
          calories={today.calories}
          proteinG={today.protein_g}
          caloriesGoal={goal.calories}
          proteinGoalG={goal.protein_g}
          mealCount={today.meals}
        />
      </section>
      <section className="card">
        <h2>Last 7 days</h2>
        <WeeklyChart days={days} goal={goal} tz={tz} />
      </section>
      <section className="card">
        <h2>Calendar</h2>
        <MonthHeatmap
          days={days}
          month={selectedMonth}
          onMonthChange={clampedSetMonth}
          minMonth={minMonth}
          goal={goal}
        />
      </section>
      <section className="card">
        <h2>Last 30 days</h2>
        <StatChips days={last30} />
      </section>
      <section className="card">
        <h2>30-day trend</h2>
        <TrendChart days={last30} goal={goal} tz={tz} />
      </section>
    </div>
  );
}
